import { Annotation } from "../../annotator/interfaces/annotation";
import { Status, todoStatuses } from "../../common/status/status";

// Org-triggered customer-normalized entity ids (Ardo / Ontex / TopsFoods).
// Reused for the loading state and the conversion pill. Edmac is config-driven
// (csv/csvEdmac strategy) and detected separately.
export const CUSTOMER_NORMALIZED_ENTITY_IDS = [
  "87d805ed-0b85-4d7f-b0a5-fe101bb07392", // Pallet Quantity (TopsFoods)
  "381fd87d-eb52-4f3f-ac9a-7e7f15e0e4d0", // Quantity
  "d4775e23-d44f-491a-a01a-7fe611685b03", // Quantity Unit (Ardo / Ontex)
  "3448cf66-9b13-4c43-92e5-6b6ce1b0b2c0", // Quantity (Ardo)
];

/**
 * The statuses in which the customer normalizations run. An export that failed
 * is corrected in the annotator too, often because a customer value (such as a
 * TopsFoods route number) is missing, so it needs them as well.
 */
export const CUSTOMER_NORMALIZATION_STATUSES: Array<Status> = [
  ...todoStatuses,
  Status.ExportFailed,
];

/**
 * The part of each annotation that the customer normalizations read, for the
 * hash that decides when they run again.
 *
 * The normalized value counts, because the customer service reads it (a
 * TopsFoods route is calculated from the normalized delivery date). It arrives
 * after the value: a new value is normalized after the change, and a typed
 * value is only a normalized value. Without it in the hash, the customer
 * service would answer for the old date and never be asked again.
 *
 * The normalized value of a field that the customer service writes itself is
 * left out, or its answer would change the hash and ask again without end. A
 * typed value of such a field stays in: the service never overwrites it.
 * Annotations without values are outputs of the service (a route number) and
 * are left out for the same reason.
 */
export const customerNormalizationInputs = (annotations: Array<Annotation>) =>
  annotations
    .filter((annotation) => annotation.values.length > 0)
    .map((annotation) => {
      const normalization = annotation.entityAnnotationNormalization;
      const typed = normalization?.normalizedMethod === "manual";
      const writtenByService =
        CUSTOMER_NORMALIZED_ENTITY_IDS.includes(annotation.entity.id) && !typed;
      return {
        id: annotation.id,
        values: annotation.values,
        index: annotation.index,
        entityId: annotation.entity.id,
        multipleGroupBlocks: annotation.multipleGroupBlocks,
        normalizedValue: writtenByService
          ? undefined
          : normalization?.normalizedValue,
      };
    });
